import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { completeOauthConnect, connectMcp, USER_MCP_QUERY_KEYS } from '../../api';
import { openOAuthPopup } from './oauth-popup';

/**
 * Drives the OAuth connect flow for a catalog `oauth` MCP: asks the BE for the authorize URL, opens
 * it in a popup, waits for the popup to report the authorization response was received, and then
 * completes the exchange through the authenticated endpoint — the step that proves to the server
 * the person finishing the flow is the one who started it. On any terminal outcome we refetch the
 * authoritative state — the same `installed` + `health` invalidation as {@link useInstallMcp} —
 * rather than trusting the popup's status. A blocked/failed exchange surfaces an error; a
 * user-dismissed popup is silent.
 */
export const useConnectMcp = () => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  const refetch = async (mcpCatalogId: string) => {
    await queryClient.invalidateQueries({ queryKey: USER_MCP_QUERY_KEYS.installed });
    await queryClient.invalidateQueries({
      queryKey: USER_MCP_QUERY_KEYS.health(mcpCatalogId),
    });
  };

  return useMutation({
    mutationFn: async ({ mcpCatalogId }: { mcpCatalogId: string }) => {
      const result = await connectMcp(mcpCatalogId);
      // 'connected' → the server refreshed still-valid tokens; there's no authorize URL and no popup
      // to open, so the MCP is already connected. Treat it as success (onSuccess refetches state).
      if (result.status === 'connected' || !result.authorizeUrl) {
        return 'success' as const;
      }
      const popup = await openOAuthPopup(result.authorizeUrl);
      if (popup === 'error' || popup === 'closed') {
        return popup;
      }
      // The popup only means the authorization response was parked server-side. Completing under
      // this session is what connects — and what proves the finisher is the starter. A completion
      // failure is a failed connect, not a failed start, so it maps to the same toast as a failed
      // exchange rather than falling through to onError's "could not start" message.
      try {
        await completeOauthConnect(popup.state);
      } catch (error) {
        logger.error('Failed to complete MCP OAuth connect:', error);
        return 'error' as const;
      }
      return 'success' as const;
    },
    onSuccess: async (result, { mcpCatalogId }) => {
      await refetch(mcpCatalogId);
      if (result === 'success') {
        notificationService.showSuccess(t('userMcpServers.useConnectMcp.mcpServerConnected'));
      } else if (result === 'error') {
        notificationService.showError(t('userMcpServers.useConnectMcp.couldNotConnectTheMcp'));
      }
      // 'closed' → the user dismissed the popup; stay silent, the refetch already reflects reality.
    },
    onError: (error) => {
      notificationService.showError(t('userMcpServers.useConnectMcp.couldNotStartTheOauth'));
      logger.error('Failed to start MCP OAuth connect:', error);
    },
  });
};
